import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Star, Quote, Loader2, Plus, X, MapPin } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { api } from "@/lib/site";

const SEED = [
    { name: "Marissa T.", location: "Roseville, CA", rating: 5, text: "Our bins used to reek in the summer heat. Ben showed up right after pickup and now they look brand new. Total game changer." },
    { name: "Devin R.", location: "Rocklin, CA", rating: 5, text: "Added the car wash on a whim and my truck came out gleaming. Professional, on time, and genuinely nice guy." },
    { name: "Priya S.", location: "Folsom, CA", rating: 5, text: "The dog-waste add-on is worth every penny. My backyard finally feels clean and my kids can actually play out there again." },
    { name: "Carlos M.", location: "Citrus Heights, CA", rating: 5, text: "Booked online in two minutes and got a confirmation call the same day. NextGen is the real deal." },
    { name: "Hannah W.", location: "Granite Bay, CA", rating: 4, text: "Great service and eco-friendly too. Bins smell amazing. Would love a monthly plan option someday!" },
    { name: "Tyler B.", location: "Sacramento, CA", rating: 5, text: "Two bins for $35 is a steal for how spotless they came out. Already told my whole street about it." },
];

export default function Reviews() {
    const [reviews, setReviews] = useState([]);
    const [open, setOpen] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/reviews");
            setReviews(data);
        } catch {
            /* ignore */
        }
    };

    useEffect(() => {
        load();
    }, []);

    const all = [...reviews, ...SEED];
    const avg = (all.reduce((s, r) => s + r.rating, 0) / all.length).toFixed(1);

    return (
        <main data-testid="reviews-page" className="bg-sand pt-[74px]">
            <section className="mx-auto max-w-7xl px-5 md:px-10 py-16 md:py-24">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
                    <Reveal className="lg:col-span-8">
                        <p className="text-sm uppercase tracking-[0.25em] text-forest/50 mb-4">Loved by neighbors</p>
                        <h1 className="font-heading font-extrabold tracking-tight text-forest text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                            Real reviews from <span className="text-sky">real curbs.</span>
                        </h1>
                    </Reveal>
                    <Reveal delay={0.1} className="lg:col-span-4">
                        <div className="rounded-3xl bg-forest text-sand p-7">
                            <div className="flex items-center gap-3">
                                <span className="font-heading font-extrabold text-5xl">{avg}</span>
                                <div className="flex flex-col">
                                    <Stars value={Math.round(avg)} className="text-sky" />
                                    <span className="text-xs text-sand/60 mt-1">{all.length} happy customers</span>
                                </div>
                            </div>
                            <button
                                data-testid="open-review-modal"
                                onClick={() => setOpen(true)}
                                className="mt-6 w-full rounded-full bg-sand text-forest py-3 font-semibold hover:bg-sky transition-colors duration-200 flex items-center justify-center gap-2"
                            >
                                <Plus size={17} /> Write a review
                            </button>
                        </div>
                    </Reveal>
                </div>

                <div className="mt-14 columns-1 md:columns-2 lg:columns-3 gap-6 [column-fill:_balance]">
                    {all.map((r, i) => (
                        <Reveal key={(r.id || "seed") + i} delay={(i % 3) * 0.06} className="mb-6 break-inside-avoid">
                            <div className="rounded-3xl bg-white border border-black/5 p-7 hover:-translate-y-1 hover:shadow-[0_18px_45px_rgba(14,56,42,0.1)] transition-[transform,box-shadow] duration-300">
                                <Quote className="text-sage" size={30} />
                                <p className="mt-4 text-forest/80 leading-relaxed">{r.text}</p>
                                <div className="mt-6 flex items-center justify-between">
                                    <div>
                                        <p className="font-heading font-bold text-forest">{r.name}</p>
                                        {r.location && (
                                            <p className="text-xs text-forest/45 flex items-center gap-1">
                                                <MapPin size={12} /> {r.location}
                                            </p>
                                        )}
                                    </div>
                                    <Stars value={r.rating} className="text-forest" />
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <AnimatePresence>
                {open && <ReviewModal onClose={() => setOpen(false)} onSaved={load} />}
            </AnimatePresence>
        </main>
    );
}

function Stars({ value, className = "" }) {
    return (
        <div className={`flex gap-0.5 ${className}`}>
            {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} size={16} fill={i < value ? "currentColor" : "none"} strokeWidth={i < value ? 0 : 1.5} />
            ))}
        </div>
    );
}

function ReviewModal({ onClose, onSaved }) {
    const [form, setForm] = useState({ name: "", location: "", rating: 5, text: "" });
    const [hover, setHover] = useState(0);
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.text) return toast.error("Add your name and a few words.");
        setLoading(true);
        try {
            await api.post("/reviews", form);
            toast.success("Thanks for the review!");
            await onSaved();
            onClose();
        } catch {
            toast.error("Could not submit. Try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <motion.div
            className="fixed inset-0 z-[60] flex items-center justify-center p-5 bg-forestdeep/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            data-testid="review-modal"
        >
            <motion.div
                onClick={(e) => e.stopPropagation()}
                initial={{ scale: 0.94, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.94, y: 20, opacity: 0 }}
                transition={{ type: "spring", stiffness: 260, damping: 22 }}
                className="w-full max-w-lg rounded-3xl bg-sand p-8 relative"
            >
                <button onClick={onClose} data-testid="close-review-modal" className="absolute right-5 top-5 text-forest/50 hover:text-forest">
                    <X size={22} />
                </button>
                <h3 className="font-heading font-extrabold text-2xl text-forest">Share your experience</h3>
                <p className="text-forest/55 text-sm mt-1">Your review shows up on this page instantly.</p>

                <form onSubmit={submit} className="mt-6 space-y-4">
                    <div className="flex gap-1">
                        {Array.from({ length: 5 }).map((_, i) => (
                            <button
                                key={i}
                                type="button"
                                data-testid={`rating-star-${i + 1}`}
                                onMouseEnter={() => setHover(i + 1)}
                                onMouseLeave={() => setHover(0)}
                                onClick={() => setForm((f) => ({ ...f, rating: i + 1 }))}
                                className="text-forest"
                            >
                                <Star size={28} fill={i < (hover || form.rating) ? "#0E382A" : "none"} strokeWidth={1.5} />
                            </button>
                        ))}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <input
                            data-testid="review-name"
                            placeholder="Your name *"
                            value={form.name}
                            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                            className="rounded-xl border border-black/10 bg-white px-4 py-3 outline-none focus:border-forest transition-colors"
                        />
                        <input
                            data-testid="review-location"
                            placeholder="City (optional)"
                            value={form.location}
                            onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                            className="rounded-xl border border-black/10 bg-white px-4 py-3 outline-none focus:border-forest transition-colors"
                        />
                    </div>
                    <textarea
                        data-testid="review-text"
                        placeholder="Tell us how it went… *"
                        rows={4}
                        value={form.text}
                        onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
                        className="w-full rounded-xl border border-black/10 bg-white px-4 py-3 outline-none focus:border-forest transition-colors resize-none"
                    />
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="submit-review"
                        className="w-full rounded-full bg-forest text-sand py-4 font-semibold hover:bg-forestdeep transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
                    >
                        {loading && <Loader2 className="animate-spin" size={18} />}
                        {loading ? "Posting…" : "Post review"}
                    </button>
                </form>
            </motion.div>
        </motion.div>
    );
}
